<template>
  <div :class="{box:isBoxed, 'top-margin-is-1':!isBoxed}" :name="instanceName + '.header'">
    <div class="columns">
      <div class="column is-12">
        <label class="label" style="margin-left:10px;">
          {{label}}
          <b-icon pack="fas" icon="star" class="fa-ss" v-if="required"></b-icon>
        </label>
      </div>
    </div>
    <div class="columns">
      <div class="column is-12">
        <div v-viewer="{title: false}" class="item-container">
          <div class="is-inline-flex iPhoto" v-if="enabled" style="margin-left:10px;">
            <input type="hidden" :data-vv-scope="scope" :id="instanceName" :name="instanceName" data-vv-validate-on="change|input" :data-vv-as="validateAs" v-validate="{'required': required}" v-model="hasFiles">
            <b-upload v-model="uploadValue"
                type="is-info"
                :multiple="multiple"
                :disabled="shouldBeDisabled"
                accept="pdf/*|image/*"
                @input="loadLocalFiles"
                drag-drop>
                <div class="upload-instructions">
                  <div class="has-text-centered">
                    <p>Drop your files here or click to upload</p>
                    <i class="fal fa-upload" style="margin-top:10px;"></i>
                  </div>
                </div>
            </b-upload>
          </div>
          <div class="is-inline-flex iPhoto" :style="getPhotoContainerStyle" v-for="(file, index) in validFiles" :key="index">
            <div :class="{'box': !showFileOnly, 'is-file-container': !showFileOnly}" class="is-flex clickable" :title="file.name">
              <b-loading :is-full-page="false" :active.sync="isLoading" :canCancel="false"></b-loading>
              <figure class="image" :class="containerDisplaySizeClass">
                <img v-if="isPdf(file)" src="@/assets/pdf.png" :style="imageDisplaySizeStyle" @click.stop.prevent="showFile(file)">
                <img v-else-if="isImage(file)" :src="file.imageData" :style="imageDisplaySizeStyle">
                <img v-else src="@/assets/file.jpg" :style="imageDisplaySizeStyle">
              </figure>
              <div class="tags has-addons extra-top-margin" v-if="enabled && !showFileOnly">
                <span class="tag is-link" v-if="file.isUploadError">{{ uploadErrorDescription }}</span>
                <span class="tag is-link" v-if="!file.isUploadError && !file.isUploading">{{ shortened(file.name) }}</span>
                <span class="tag is-link" v-if="!file.isUploadError && file.isUploading">upload progress: {{ file.uploadPercentage }}%</span>
                <span v-if="enabled" class="tag is-delete" @click="deleteFile(selectedFiles, file)"></span>
                <i class="tag fal fa-download clickable has-text-primary" title="Click to download this file directly." @click="onDownloadFile(file)"></i>
              </div>
            </div>
          </div>
          <br/>
        </div>
      </div>
    </div>
    <span v-show="errors.has(validationName)" class="help is-danger">
      <i v-show="errors.has(validationName)" class="fas fa-exclamation-triangle"></i> {{ errors.first(validationName) }}
    </span>
    <portal to="global-modal-portal" :disabled="!portalizePdfViewer">
      <b-modal :active.sync="isPdfViewerActive" scroll="keep" :has-modal-card="true" v-if="isPdfViewerActive" class="my-modal" :class="applyZindexClass">
        <div class="modal-card">
          <header class="modal-card-head">
              <p class="modal-card-title">{{selectedFile.name}}</p>
          </header>
          <section class="modal-card-body">
            <pdf
              ref="pdfViewer"
              v-for="i in selectedFile.numPages"
              :key="i"
              :src="selectedFile.src"
              :page="i"
              style="display: contents; width: 99%">
              <br/>
            </pdf>
          </section>
          <footer class="modal-card-foot">
            <button class="button" @click.prevent="closePdf">Close</button>
            <button class="button" @click.prevent="onDownloadFile(selectedFile)" v-if="selectedFile.imageData.startsWith('http')">Download</button>
            <button class="button" @click.prevent="onPrint">Print</button>
          </footer>
        </div>
      </b-modal>
    </portal>
  </div>
</template>

<script>

import axios from 'axios'
import pdf from 'vue-pdf'

export default {
  inject: {
    $validator: '$validator'
  },
  components: {
    pdf
  },
  name: 'FileSelector',
  props: {
    value: {
      type: Array,
      default: null
    },
    label: {
      type: String,
      default: 'File'
    },
    validateAs: {
      type: String,
      default: 'Item'
    },
    required: {
      type: Boolean,
      default: false
    },
    enabled: {
      type: Boolean,
      default: true
    },
    multiple: {
      type: Boolean,
      default: true
    },
    isBoxed: {
      type: Boolean,
      default: true
    },
    scope: {
      type: String,
      default: null
    },
    displaySize: {
      type: String,
      default: '64'
    },
    showFileOnly: {
      type: Boolean,
      default: false
    },
    usePortalViewer: {
      type: Boolean,
      default: true
    },
    token: {
      type: String,
      default: null
    },
    destinationUrl: {
      type: String,
      default: ''
    },
    applyZindexClass: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      uploadValue: null,
      selectedFiles: this.value,
      instanceName: 'file-select-' + this.$uuid.v4(),
      isPdfViewerActive: false,
      isLoading: false,
      selectedFile: null,
      hasFiles: (this.value && this.value.length > 0) ? 'YES' : null
    }
  },
  computed: {
    filesValid: function () {
      return this.selectedFiles && this.selectedFiles.length > 0
    },
    portalizePdfViewer: function () {
      return this.usePortalViewer
    },
    shouldBeDisabled: function () {
      if (this.multiple === true) {
        return false
      } else {
        if (this.validFiles.length > 0) {
          return true
        }
      }
      return false
    },
    getPhotoContainerStyle: function () {
      return {
        width: this.enabled ? 'auto' : '100%'
      }
    },
    validFiles: function () {
      return this.selectedFiles ? this.selectedFiles.filter((x) => x.loaded === true && (!x.status || x.status !== 'Deleted')) : []
    },
    imageDisplaySizeStyle: function () {
      const key = 'max-height'
      var result = {}
      result[key] = this.displaySize + 'px'
      return result
    },
    imageDisplaySizeClass: function () {
      const key = 'is-' + this.displaySize + 'x' + this.displaySize
      var result = {}
      result[key] = true
      return result
    },
    containerDisplaySizeClass: function () {
      const key = 'is-' + this.displaySize + 'xauto'
      var result = {}
      result[key] = true
      return result
    },
    validationName: function () {
      return this.scope ? this.scope + '.' + this.instanceName : this.instanceName
    },
    showErrors: function () {
      return this.$validator.errors
    }
  },
  methods: {
    shortened (name) {
      return this.$lodash.truncate(name, { length: 15, separator: ' ' })
    },
    isPdf (file) {
      return file.fileType === 'application/pdf'
    },
    isImage (file) {
      return (file.fileType === 'image/png' || file.fileType === 'image/jpg' || file.fileType === 'image/jpeg' || file.fileType === 'image/gif')
    },
    showFile (file) {
      this.isLoading = true
      var that = this
      this.$nextTick(() => {
        const completeUrl = process.env.VUE_APP_EAG_ROOT_API + 'document/download?url=' + encodeURI(file.imageData)
        file.src = pdf.createLoadingTask(file.imageData.startsWith('http') ? completeUrl : file.imageData)
        file.src.promise.then(pdf => {
          file.numPages = pdf.numPages
          that.selectedFile = file
          that.isPdfViewerActive = true
          that.isLoading = false
        })
      })
      return false
    },
    closePdf () {
      this.isPdfViewerActive = false
    },
    onPrint: function () {
      var viewerRef = this.$refs.pdfViewer
      if (!viewerRef || (Array.isArray(viewerRef) && viewerRef.length === 0)) {
        // Nothing is mounted yet; skip the print call and exit safely
        alert('Printing support is not available in this browser.')
        return
      }

      const target = Array.isArray(viewerRef) ? viewerRef[0] : viewerRef
      if (target && typeof target.print === 'function') {
        target.print(100)
      }
    },
    onDownloadFile: function (file) {
      const completeUrl = process.env.VUE_APP_EAG_ROOT_API + 'document/download?url=' + encodeURI(file.imageData)
      window.location = completeUrl
    },
    deleteFile (list, file) {
      var fileIndex = list.findIndex((x) => x.name === file.name)
      if (file.status) {
        this.$set(file, 'status', 'Deleted')
      } else {
        list.splice(fileIndex, 1)
      }
      this.$emit('input', this.selectedFiles)
      this.hasFiles = (this.validFiles.length > 0) ? 'YES' : null
    },
    loadLocalFiles (fileList) {
      if (!Array.isArray(fileList)) {
        fileList = [fileList]
      }
      fileList.forEach(function (file, index) {
        const found = this.selectedFiles.find(x => x.name === file.name && x.size === file.size)
        if (!file.imageData && !found) {
          var uniqueFileName = this.$uuid.v4() + this.getFileExtension(file.name)
          uniqueFileName = uniqueFileName.replace(/-/g, '')
          var reader = new FileReader()
          reader.onload = (e) => {
            var clone = { size: file.size, name: file.name, type: file.type, fileType: file.type }
            clone.imageData = e.target.result
            clone.loaded = true
            clone.isUploaded = false
            clone.isUploading = false
            clone.uploadPercentage = 0
            clone.isUploadError = false
            clone.uploadErrorDescription = ''
            clone.uniqueId = uniqueFileName
            clone.status = 'Active'
            this.selectedFiles.push(clone)
            this.uploadFile(file, uniqueFileName, clone)
          }
          reader.readAsDataURL(file)
        }
      }, this)
    },
    getFileExtension: function (fileName) {
      return '.' + fileName.split('.').pop()
    },
    resetUploadStatus: function (clone) {
      clone.uploadPercentage = 0
      clone.isUploading = true
      clone.isUploadError = false
      clone.uploadErrorDescription = ''
    },
    uploadFile: function (file, uniqueName, clone) {
      this.resetUploadStatus(clone)
      var formData = new FormData()
      formData.append(uniqueName, file)
      axios.post(this.destinationUrl, formData, {
        params: {
          token: this.token
        },
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: progressEvent => {
          clone.uploadPercentage = ((progressEvent.loaded / progressEvent.total) * 100).toFixed(2)
        }
      }).then((response) => {
        clone.isUploading = false
        const item = response.data.results[0]
        if (!item.isValid) {
          clone.isUploadError = true
          clone.uploadErrorDescription = item.error
        } else {
          clone.isUploaded = true
          clone.uploadErrorDescription = ''
        }
      }).catch((error) => {
        clone.isUploadError = true
        clone.uploadErrorDescription = error.message
      })
    }
  },
  watch: {
    selectedFiles: function (newValue, oldValue) {
      if (this.enabled) {
        this.$emit('input', newValue)
        if (this.required && this.$validator) {
          this.$validator.validate(this.validationName)
        }
      }
    },
    value: function (newValue, oldValue) {
      if (newValue === null) {
        this.selectedFiles = []
      } else {
        this.selectedFiles = newValue
      }
      this.hasFiles = (this.validFiles.length > 0) ? 'YES' : null
    },
    isPdfViewerActive: function (newValue, oldValue) {
      if (oldValue === true && newValue === false) {
        document.body.classList.toggle('is-noscroll', false)
      }
    }
  },
  mounted: function () {

  }
}

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

.iPhoto {
  margin: 5px;
}

.item-container {
  display: flex;
  flex-wrap: wrap;
}

.is-file-container {
  position: relative;
  width: 100%;
  flex-direction: column;
  align-items: center;
}

.columns {
  display:flex;
}

.column {
  padding:0 !important
}

.top-margin-is-1 {
  margin-top:12px
}

.is-128xauto {
  display: -webkit-box;
  height:128px;
  width:auto;
  align-items: center;
  justify-content: center
}

.is-64xauto {
  display: -webkit-box;
  height:64px;
  width:auto;
  align-items: center;
  justify-content: center
}

.image img {
  display: block;
  height:auto;
  width:auto;
}

.upload-instructions {
  width:118px;
  height:97px;
  margin:1em;
}

.extra-top-margin {
  margin-top:10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.my-modal .modal-card-body {
  position:relative;
  max-height: 65vh; /* max 80% of the viewport height */
  max-width: 960px; /* max 80% of the viewport height */
  overflow-y: auto;
}

@media screen and (min-width: 769px), print {
  .my-modal .modal-card-body {
    position:relative;
    width: 99vw !important;
    max-height: 80vh; /* max 80% of the viewport height */
    max-width: 960px; /* max 80% of the viewport height */
    overflow-y: auto;
  }
}

@media screen and (min-width: 769px), print {
  .modal-content, .modal-card {
      position:relative;
      width: 99% !important;
  }
}

.completed-step-zindex {
  z-index: 50000 !important;
}
</style>
